import { ref, computed, getCurrentInstance } from 'vue';
import { gql } from 'graphql-request';
import { buildUserFeedbackPayload } from '../services/buildUserFeedbackPayload.js';
import { assertFeedbackContract } from '../services/feedbackContract.js';
import { Capacitor } from '@capacitor/core';
import { AppReview } from '@capawesome/capacitor-app-review';

// const APP_STORE_REVIEW_DELAY_MS = 1500;
// const APP_STORE_REVIEW_MIN_RATING = 3;

export function useFeedback() {
  const vm = getCurrentInstance().proxy;
  const store = vm.$store;

  const feedbackLoading = ref(false);

  const formSent = computed(() => store.getters['profile/feedback/formSent']);
  const rating = computed(() => store.getters['profile/feedback/rating']);
  const text = computed(() => store.getters['profile/feedback/text']);

  const setShowFeedback = val => store.dispatch('profile/feedback/setShowFeedback', val);
  const setDialog = val => store.dispatch('profile/dashboard/setDialog', val);
  const setFormSent = val => store.dispatch('profile/feedback/setFormSent', val);
  const setText = val => store.dispatch('profile/feedback/setText', val);
  const setRating = val => store.dispatch('profile/feedback/setRating', val);

  async function resetFeedbackTimer() {
    const currentTime = new Date();
    const value = currentTime.setMonth(currentTime.getMonth() + 3);

    const mutation = gql`
      mutation setUserAppData($name: String!, $value: String!) {
        setUserAppData(name: $name, value: $value)
      }
    `;

    const token =
      vm.$auth?.token || store.getters['auth/accessToken'] || store.getters['apiToken/token'] || '';
    const sendAuth = store.state?.general?.sendAuthorizationHeader !== false;
    const headers = token && sendAuth ? { authorization: `Bearer ${token}` } : {};

    try {
      await vm.$graphql.appSettings.request(
        mutation,
        { name: 'feedbackOpenAt', value: value.toString() },
        headers
      );
    } catch (err) {
      vm.handleError({ error: err, code: '0x00000045' });
    }
  }

  async function sendFeedback() {
    feedbackLoading.value = true;

    const payload = buildUserFeedbackPayload({
      rating: rating.value,
      text: text.value,
      profileId: vm.$auth.user.profileId,
      isMobileApp: vm.isMobileApp,
      platformId: vm.platformId,
      isBenefitActive: vm.$auth.user.benefit.active,
      partnerData: store.getters['profile/dashboard/partnerData'],
      domainRegion: vm.$domainRegion,
      isTestEnvironment: import.meta.env.VITE_GTM_ENABLED === 'false'
    });

    assertFeedbackContract(payload);

    try {
      if (!(await store.dispatch('soulodyApi/ensureValidToken'))) {
        feedbackLoading.value = false;
        return;
      }

      await vm.$soulodyApi.post('user-feedback', payload);

      await setFormSent(true);
      feedbackLoading.value = false;
      await setText('');
      await resetFeedbackTimer();
      vm.$gtm.push({ event: 'form_sent_success', 'form.name': 'feedback' });
      if (
        import.meta.env.MODE === 'production' &&
        Capacitor.isNativePlatform() &&
        rating.value > 3
      ) {
        await AppReview.requestReview();
      }
    } catch (err) {
      feedbackLoading.value = false;
      vm.handleError({ error: err, code: '0x00000031' });
    }
  }

  const feedbackButtons = computed(() => {
    if (formSent.value) {
      return [
        {
          events: { click: () => setDialog(false) },
          text: vm.$t('done')
        }
      ];
    }
    return [
      {
        type: 'sendButton',
        props: { loading: feedbackLoading.value },
        events: { click: () => sendFeedback() },
        text: vm.$t('send')
      }
    ];
  });

  return {
    feedbackLoading,
    formSent,
    rating,
    text,
    setShowFeedback,
    setDialog,
    setFormSent,
    setText,
    setRating,
    resetFeedbackTimer,
    sendFeedback,
    feedbackButtons
  };
}
